// pages/index.tsx
import EditorTabs from '@/components/editorTabs';
import FileManager from '@/components/fileManager';
import GuideTour from '@/components/guideTour';
import JobSubmissionDialog from '@/components/JobSubmissionDialog';
import {
  shareWithMeService,
} from '@/utils/shareWithMeService';
import OutputTabs from '@/components/outputTabs';
import LLMManager from '@/components/llmManager';
import Terminal from '@/components/terminal';
import AnnouncementNotification from '@/components/announcementNotification';
import AcceptShareDialog from '@/components/acceptShareDialog';
import ShareNotificationPanel from '@/components/shareNotificationPanel';
import * as apiAdmin from '@/api/admin';
import { BottomTabsKey, useTabKeyStore } from '@/utils/bottomTabsKeyStore';
import { FileEditorMapService } from '@/utils/fileEditorMapService';
import useFileStore from '@/utils/fileStore';
import { useJobStore } from '@/utils/jobService';

import { Database as editorDb } from '@/utils/persistence';
import { saveFileDialogService } from '@/utils/saveFileDialogService';
import { useSplitterStore } from '@/utils/splitterStore';
import { useKeyPress } from 'ahooks';
import type { TourProps } from 'antd';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { App, Splitter, Tabs } from 'antd';
import type { LLMDropEvent, LLMDropItem } from '@/types/llmDrop';
import { useTranslation } from 'react-i18next';
import { useActivate } from 'react-activation'
import BootSpinner from '@/components/bootSpinner';

import GuideImage1 from '@/assets/guide/tutorial_1.png';
import GuideImage2 from '@/assets/guide/tutorial_2.png';
import GuideImage3 from '@/assets/guide/tutorial_3.png';
import GuideImage4 from '@/assets/guide/tutorial_4.png';
import GuideImage5 from '@/assets/guide/tutorial_5.png';
import GuideImage6 from '@/assets/guide/tutorial_6.png';
import GuideImage7 from '@/assets/guide/tutorial_7.png';
import PlatformGuideGUI from '@/assets/guide/platform_tutorial_gui.png';
import PlatformGuideEditor from '@/assets/guide/platform_tutorial_editor.png';

const Home: React.FC = () => {
  const { message } = App.useApp();
  const [searchParams] = useSearchParams();
  const openFolderOrFile = useFileStore((state) => state.openFolderOrFile);
  const setSelectedKeys = useFileStore((state) => state.setSelectedKeys);
  const fileManagerDivRef = useRef<HTMLDivElement>(null);
  const fileTree = useFileStore(state => state.fileTree);
  const { t } = useTranslation('common');

  // Announcement state
  const [announcement, setAnnouncement] = useState<apiAdmin.LatestAnnouncementData | null>(null);

  // Share acceptance state
  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [pendingShareId, setPendingShareId] = useState<string | null>(null);
  const [llmDropEvent, setLlmDropEvent] = useState<LLMDropEvent | undefined>(undefined);
  const [isDraggingOverLLM, setIsDraggingOverLLM] = useState(false);

  // Tour component related refs
  const editorTabsRef = useRef<HTMLDivElement>(null);
  const smartAssistantRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // 检查并显示通知
  useEffect(() => {
    const checkAndShowAnnouncement = async () => {
      try {
        const response = await apiAdmin.GetLatestAnnouncement();
        if (response?.ok) {
          setAnnouncement(response.data ?? null);
        }
      } catch (error) {
        console.error('Failed to fetch announcement:', error);
      }
    };

    checkAndShowAnnouncement();
  }, []);

  // 检查并处理待接受的分享
  useEffect(() => {
    const shareId = sessionStorage.getItem('pendingShareId');
    if (shareId) {
      // Remove from sessionStorage immediately to prevent re-showing
      sessionStorage.removeItem('pendingShareId');
      // Set state to show dialog
      setPendingShareId(shareId);
      setShareDialogVisible(true);
    }
  }, []);

  const openWorkDir = useCallback((workDir: string) => {
    window.history.replaceState({}, '', window.location.pathname);
    openFolderOrFile(workDir).then(() => {
      setSelectedKeys([workDir]);
      setTimeout(() => {
        const treeWrapper = document.getElementById('tree-wrapper');
        if (!treeWrapper) return;
        const selectedElement = treeWrapper.querySelector(`[data-key="${CSS.escape(workDir)}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    });
  }, [openFolderOrFile, setSelectedKeys]);

  // 处理 workDir 参数
  useEffect(() => {
    const workDir = new URLSearchParams(document.location.search).get('workDir');
    if (workDir) {
      openWorkDir(workDir);
    }
  }, [openWorkDir]);

  useActivate(() => {
    const workDir = new URLSearchParams(document.location.search).get('workDir');
    if (workDir) {
      openWorkDir(workDir);
    }
  })


  // Tour 步骤配置
  // Tour steps configuration - Guide users through the platform features

  const protomiaTourSteps: TourProps['steps'] = [
    {
      title: t('tour.action_buttons_title'),
      cover: (
        <img
          src={GuideImage1}
          style={{ minWidth: 760, borderRadius: 8 }}
        />
      ),
      description: t('tour.action_buttons_desc'),
    },
    {
      title: t('tour.working_dir_title'),
      cover: (
        <img
          src={GuideImage2}
          style={{ minWidth: 760, borderRadius: 8 }}
        />
      ),
      description: t('tour.working_dir_desc'),
    },
    {
      title: t('tour.analyze_title'),
      cover: (
        <img
          src={GuideImage3}
          style={{ minWidth: 760, borderRadius: 8 }}
        />
      ),
      description: t('tour.analyze_desc'),
    },
    {
      title: t('tour.workflows_title'),
      cover: (
        <img
          src={GuideImage4}
          style={{ minWidth: 760, borderRadius: 8 }}
        />
      ),
      description: t('tour.workflows_desc'),
    },
    {
      title: t('tour.mlatom_title'),
      cover: (
        <img
          src={GuideImage5}
          style={{ minWidth: 760, borderRadius: 8 }}
        />
      ),
      description: t('tour.mlatom_desc'),
    },
    {
      title: t('tour.chat_history_title'),
      cover: (
        <img
          src={GuideImage6}
          style={{ minWidth: 760, borderRadius: 8 }}
        />
      ),
      description: t('tour.chat_history_desc'),
    },
    {
      title: t('tour.save_chat_title'),
      cover: (
        <img
          src={GuideImage7}
          style={{ minWidth: 760, borderRadius: 8 }}
        />
      ),
      description: t('tour.save_chat_desc'),
    },
  ];

  // Tour steps configuration - Guide users through the platform features
  const tourSteps: TourProps['steps'] = [
    {
      title: t('tour.file_manager_title'),
      description: (
        <div>
          <p><strong>{t('tour.file_manager_intro')}</strong></p>
          <ul style={{ paddingLeft: 20 }}>
            <li>• {t('tour.file_manager_item1')}</li>
            <li>• {t('tour.file_manager_item2')}</li>
            <li>• {t('tour.file_manager_item3')}</li>
            <li>• {t('tour.file_manager_item4')}</li>
          </ul>
        </div>
      ),
      target: () => fileManagerDivRef.current || document.body,
      placement: 'right',
    },
    {
      title: t('tour.code_editor_title'),
      cover: (
        <img src={PlatformGuideEditor} />
      ),
      description: (
        <div>
          <p><strong>{t('tour.code_editor_intro')}</strong></p>
          <ul style={{ paddingLeft: 20 }}>
            <li>• {t('tour.code_editor_item1')}</li>
            <li>• {t('tour.code_editor_item2')}</li>
            <li>• {t('tour.code_editor_item3')}</li>
          </ul>
        </div>
      ),
      target: () => editorTabsRef.current || document.body,
      placement: 'bottom',
    },
    {
      title: t('tour.gui_viewer_title'),
      cover: (
        <img src={PlatformGuideGUI} />
      ),
      description: (
        <div>
          <p><strong>{t('tour.gui_viewer_intro')}</strong></p>
          <ul style={{ paddingLeft: 20 }}>
            <li>• {t('tour.gui_viewer_item1')}</li>
            <li>• {t('tour.gui_viewer_item2')}</li>
            <li>• {t('tour.gui_viewer_item3')}</li>
          </ul>
        </div>
      ),
      target: () => editorTabsRef.current || document.body,
      placement: 'bottom',
    },
    {
      title: t('tour.terminal_title'),
      description: (
        <div>
          <p><strong>{t('tour.terminal_intro')}</strong></p>
          <ul style={{ paddingLeft: 20 }}>
            <li>• {t('tour.terminal_item1')}</li>
            <li>• {t('tour.terminal_item2')}</li>
            <li>• {t('tour.terminal_item3')}</li>
          </ul>
          <p style={{ color: '#1890ff', marginTop: 8 }}>
            {t('tour.terminal_tip')}
          </p>
        </div>
      ),
      target: () => bottomRef.current || document.body,
      placement: 'top',
    },
    {
      title: t('tour.output_title'),
      description: (
        <div>
          <p><strong>{t('tour.output_intro')}</strong></p>
          <ul style={{ paddingLeft: 20 }}>
            <li>• {t('tour.output_item1')}</li>
            <li>• {t('tour.output_item2')}</li>
            <li>• {t('tour.output_item3')}</li>
            <li>• {t('tour.output_item4')}</li>
            <li>• {t('tour.output_item5')}</li>
          </ul>
          <p style={{ color: '#1890ff', marginTop: 8 }}>
            {t('tour.output_tip')}
          </p>
        </div>
      ),
      target: () => bottomRef.current || document.body,
      placement: 'top',
    },
    {
      title: t('tour.ai_title'),
      description: (
        <div>
          <p><strong>{t('tour.ai_intro')}</strong></p>
          <ul style={{ paddingLeft: 20 }}>
            <li>• {t('tour.ai_item1')}</li>
            <li>• {t('tour.ai_item2')}</li>
            <li>• {t('tour.ai_item3')}</li>
            <li>• {t('tour.ai_item4')}</li>
            <li>• {t('tour.ai_item5')}</li>
          </ul>
        </div>
      ),
      target: () => smartAssistantRef.current || document.body,
      placement: 'left',
    },
    {
      title: t('tour.start_title'),
      description: (
        <div>
          <p><strong>{t('tour.start_intro')}</strong></p>
          <div style={{ backgroundColor: '#f6ffed', padding: 12, borderRadius: 6, margin: '12px 0' }}>
            <p style={{ margin: 0, color: '#52c41a' }}><strong>{t('tour.quick_start')}</strong></p>
            <ol style={{ paddingLeft: 20, margin: '8px 0' }}>
              <li>{t('tour.start_step1')}</li>
              <li>{t('tour.start_step2')}</li>
              <li>{t('tour.start_step3')}</li>
              <li>{t('tour.start_step4')}</li>
              <li>{t('tour.start_step5')}</li>
            </ol>
          </div>
          <p style={{ color: '#8c8c8c', fontSize: 12 }}>
            {t('tour.start_hint')}
          </p>
        </div>
      ),
      target: null, // Center display
      placement: 'center',
    },
  ];


  const panes = useFileStore(state => state.panes);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasUnsavedChanges = panes.some(pane => pane.modified);
      const editorMapService = FileEditorMapService.getInstance();
      if (hasUnsavedChanges) {
        panes.forEach(async (pane) => {
          if (pane.modified) {
            const content = editorMapService.getEditorRef(pane.key)?.getContent();
            console.log(content)
            try {
              await editorDb?.updateFileData(pane.key, {
                key: pane.key,
                path: pane.path,
                content: content ?? "",
              });
            } catch (e) {
              console.log("file not found in db", e)
            }

          }
        });
      }

      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [panes]);




  const openJobSubmission = () => {
    if (!activePanes[0]) {
      message.error(t('main.no_file_selected'));
      return;
    }
    const selectedFilePath = activePanes[0].path;
    if (!selectedFilePath) {
      message.error(t('main.no_file_selected'));
      return;
    }
    if (selectedFilePath?.startsWith('tmp_')) {
      message.info(t('main.save_before_submit'));
      return;
    }
    handleSave();
    setjobSubmissionVisible(true);
  }

  // key bindings
  const [jobSubmissionVisible, setjobSubmissionVisible] = useState(false);
  const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  useKeyPress(isMac ? ['meta.r'] : ['ctrl.r'], (e) => {
    e.preventDefault();
    openJobSubmission();
  });

  const activePanes = useFileStore(state => state.activePanes);
  const onFileSave = useFileStore(state => state.onFileSave);

  const handleSave = async () => {
    const pane = activePanes?.[0];
    if (!pane?.key) return false;
    if (pane?.path) {
      const success = await onFileSave(pane.key);
      return success;
    } else {
      saveFileDialogService.show(pane.key);
      return true;
    }
  }

  useKeyPress(isMac ? ['meta.s'] : ['ctrl.s'], (e) => {
    e.preventDefault();
    handleSave();
  });

  const setBottomTabsActiveKey = useTabKeyStore(state => state.setBottomTabsActiveKey);
  const bottomTabsActiveKey = useTabKeyStore(state => state.bottomTabsActiveKey);

  const onBottomTabsChange = (key: string) => {
    setBottomTabsActiveKey(key as BottomTabsKey.TERMINAL);
  }

  const jobs = useJobStore(state => state.jobs);

  // Splitter state management
  const {
    mainSizes,
    setMainSizes,
    verticalSizes,
    setVerticalSizes
  } = useSplitterStore();

  const TerminalTabs = useCallback(() => {
    return <Terminal />
  }, [])

  const hasJobs = Object.keys(jobs).length > 0;

  const bottomFeatureTabs = useMemo(() => ([{
    key: 'terminal',
    label: t('main.terminal'),
    children: <TerminalTabs />
  }, {
    key: 'output',
    label: t('main.output'),
    disabled: !hasJobs,
    children: <OutputTabs />,
  }]), [TerminalTabs, hasJobs, t])

  useEffect(() => {
    if (bottomTabsActiveKey === 'output' && !hasJobs) {
      setBottomTabsActiveKey(BottomTabsKey.TERMINAL)
    }
  }, [bottomTabsActiveKey, hasJobs, setBottomTabsActiveKey])

  const aitomicShow = useFileStore(state => state.aitomicShow);

  // Handle share acceptance success
  const handleShareAcceptSuccess = async (filePath: string) => {
    // Navigate to the shared file in the file manager
    await shareWithMeService.refresh()
    openFolderOrFile(filePath);
    message.success(t('main.share_accepted'));
  };

  const handleShareDialogClose = () => {
    setShareDialogVisible(false);
    setPendingShareId(null);
  };

  const handleDropToLLMManager = useCallback((items: LLMDropItem[]) => {
    setLlmDropEvent({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      items,
    });
  }, []);

  if (!fileTree) {
    return <div className={`
            flex flex-col items-center justify-center
            fixed inset-0 bg-white dark:bg-[#252526] z-50 h-screen w-screen
        `}>
      <BootSpinner size={'large'} />
    </div>
  }


  return (
    <div className=' w-full h-full'>
      {/* Announcement Notification */}
      {announcement && (
        <AnnouncementNotification
          userAnnouncement={announcement.user_announcement}
          globalAnnouncement={announcement.global_announcement}
          onClose={() => setAnnouncement(null)}
        />
      )}

      {/* Accept Share Dialog */}
      {pendingShareId && (
        <AcceptShareDialog
          visible={shareDialogVisible}
          shareId={pendingShareId}
          onClose={handleShareDialogClose}
          onSuccess={handleShareAcceptSuccess}
        />
      )}

      <Splitter
        className=' w-full h-full'
        onResize={setMainSizes}
      >
        <Splitter.Panel size={mainSizes[0]} collapsible >
          <div ref={fileManagerDivRef} className='h-full flex flex-col'>
            <div className='flex-1 min-h-0'>
              <FileManager
                onDropToLLMManager={handleDropToLLMManager}
                onDragOverLLMManager={setIsDraggingOverLLM}
              />
            </div>
            <ShareNotificationPanel />
          </div>
        </Splitter.Panel>
        <Splitter.Panel className='bottom-area' size={mainSizes[1]}>
          <Splitter
            orientation="vertical"
            className=' h-full'
            onResize={setVerticalSizes}
          >
            <Splitter.Panel size={verticalSizes[0]} className='relative'>
              <div ref={editorTabsRef} className=' h-full'>
                <EditorTabs openJobSubmission={openJobSubmission} />
              </div>
              <JobSubmissionDialog onClose={() => setjobSubmissionVisible(false)} visible={jobSubmissionVisible} />
            </Splitter.Panel>
            <Splitter.Panel size={verticalSizes[1]} collapsible={{ start: true, end: true, showCollapsibleIcon: true }}>
              <div className=' w-full h-full' ref={bottomRef}>
                <Tabs
                  hideAdd
                  tabBarStyle={{ marginLeft: 10 }}
                  onChange={onBottomTabsChange}
                  activeKey={bottomTabsActiveKey}
                  className=' h-full'
                  size='small'
                  items={bottomFeatureTabs}
                />
              </div>
            </Splitter.Panel>
          </Splitter>
        </Splitter.Panel>
        <Splitter.Panel size={aitomicShow ? mainSizes[2] : "0%"}>
          <div ref={smartAssistantRef} className=' h-full w-full'>
            <LLMManager
              aitomicShow={aitomicShow}
              externalDropEvent={llmDropEvent}
              isDragOverByFileManager={isDraggingOverLLM}
            />
          </div>
        </Splitter.Panel>
      </Splitter>

      {/* Use the new GuideTour component */}
      <GuideTour
        tourId="protomia"
        steps={protomiaTourSteps}
        autoStart={false}
        showFloatButton={true}
        buttonPosition={{ right: 24, bottom: 24 }}
      />

      <GuideTour
        tourId="workplace"
        steps={tourSteps}
        autoStart={true}
        showFloatButton={true}
        buttonPosition={{ right: 24, bottom: 24 }}
      />
    </div>
  );
};

export default Home;